<template>
  <div class="standalone-order-modal">
    <template v-if="payFail">
      <PayFail :needRepayBtn="false"></PayFail>
      <div class="standalone-order-modal__divider standalone-order-modal__divider--top"></div>
      <PaySuccTip class="standalone-order-modal__tip" />
    </template>
    <template v-else-if="paySucc">
      <PaySucc :title="price.string" :desc="$t('ding-yue-orderrightsname', [props.rights_name])" />
      <div class="standalone-order-modal__divider"></div>
      <PaySuccTip class="standalone-order-modal__tip" />
    </template>
    <template v-else>
      <Alert v-if="props.tip" type="warning" class="standalone-order-modal__alert">
        {{ props.tip }}
      </Alert>
      <h3 class="standalone-order-modal__title">{{ $t('qing-yong-zhi-fu-bao-sao-ma-zhi-fu') }}</h3>
      <div class="standalone-order-modal__qrcode">
        <div class="standalone-order-modal__qrcode-inner">
          <iframe
            :src="props.qr_code_url"
            class="standalone-order-modal__qrcode-iframe"
            frameborder="0"
            scrolling="no"
          ></iframe>
        </div>
      </div>
      <div class="standalone-order-modal__rights-name">
        {{ props.rights_name }}
      </div>
      <div class="standalone-order-modal__price">{{ price.string }}</div>
      <div class="standalone-order-modal__valid-time">{{ $t('you-xiao-qi-0') }}{{ dateRange }}</div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { getOrderClientDetail } from '@rendora/common/entities/order';
import { ORDER_STATUS, type ORDER_BEHAVIOR } from '@rendora/common/types/enums';
import { formatPrice } from '@rendora/common/utils/transform';
import { Alert } from '@rendora/design/components/alert/index';
import dayjs from 'dayjs';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import PayFail from '../PayFail.vue';
import PaySucc from '../PaySuccess.vue';
import PaySuccTip from '../PaySuccessTip.vue';

const props = defineProps<{
  behavior: ORDER_BEHAVIOR;
  id: number;
  pay_fee: number;
  qr_code_url: string;
  status: ORDER_STATUS;
  rights_name: string;
  start_timestamp: number;
  expired_timestamp: number;
  tip: string;
}>();

const paySucc = ref(false);
const payFail = ref(false);

let lid: number = 0;

const dateRange = computed(() => {
  const start = dayjs(props.start_timestamp * 1000).format('YYYY.MM.DD');
  const end = dayjs(props.expired_timestamp * 1000).format('YYYY.MM.DD');
  return `${start} - ${end}`;
});

const price = computed(() => formatPrice(props.pay_fee));
const abort = ref<AbortController>();

async function checkStatus() {
  // 退出正在进行 pending 的请求
  if (abort.value) {
    abort.value.abort();
  }
  abort.value = new AbortController();
  const res = await getOrderClientDetail(props.id);
  if (res.data) {
    if ([ORDER_STATUS.PAID].includes(res.data.status)) {
      paySucc.value = true;
      clearInterval(lid);
    } else if ([ORDER_STATUS.FAIL].includes(res.data.status)) {
      payFail.value = true;
    }
  }
}

function startLoop() {
  clearInterval(lid);
  lid = window.setInterval(checkStatus, 5000);
}

onMounted(() => {
  startLoop();
});

onBeforeUnmount(() => {
  clearInterval(lid);
  abort.value?.abort();
});
</script>

<style lang="scss" scoped>
.standalone-order-modal {
  // 主容器样式
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background-color: var(--x-back-0);
  border-radius: var(--x-radius-large);
  justify-content: center;
  align-items: center;
  padding: 24px 24px 44px 24px;

  // 标题样式
  &__title {
    color: var(--x-text-1);
    font-weight: var(--x-weight-medium);
    margin-top: 20px;
  }

  // 警告提示样式
  &__alert {
    width: 100%;
  }

  // 分割线样式
  &__divider {
    width: 92%;
    height: 1px;
    background-color: var(--x-back-3);
    margin: 0 auto;

    &--top {
      margin-top: 20px;
    }
  }

  // 提示信息样式
  &__tip {
    margin: 28px 0;
  }

  // 二维码容器样式
  &__qrcode {
    height: 140px;
    width: 140px;
    background-color: var(--x-back-2);
    overflow: hidden;
    border-radius: var(--x-radius-medium);
    padding: 5px;
    margin-top: 20px;

    &-inner {
      background-color: var(--x-back-0);
      padding: 5px;
      height: 100%;
    }

    &-iframe {
      width: 150px;
      transform: scale(calc(120 / 150));
      transform-origin: left top;
    }
  }

  // 权益名称样式
  &__rights-name {
    color: var(--x-warning-8);
    font-size: 14px;
    font-weight: var(--x-weight-medium);
    margin-top: 20px;
  }

  // 价格样式
  &__price {
    color: var(--x-error-6);
    font-size: 28px;
    line-height: 42px;
    margin-bottom: 6px;
  }

  // 有效期样式
  &__valid-time {
    color: var(--x-text-2);
    font-size: 14px;
  }
}
</style>
